import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Send, User, Building2, Phone, Mail, MapPin, Globe, Plane } from 'lucide-react';
import api from '../services/api';
import { formatApiError } from '../utils/apiError';

const FormularioTallerItinerante = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Datos Personales
    nombre_completo: '',
    documento_identidad: '',
    telefono: '',
    pais: '',
    provincia: '',
    direccion: '',
    email: '',
    // Datos del Taller
    razon_social: '',
    numero_anac: '',
    pagina_web: '',
    aerodromo: '',
    // Preferencia de contacto
    contacto_email: true,
    contacto_telefono: false,
    // Comentarios adicionales
    comentarios: ''
  });

  const paises = [
    'Uruguay',
    'Argentina',
    'Brasil',
    'Chile',
    'Paraguay',
    'Bolivia',
    'Perú',
    'Colombia',
    'Ecuador',
    'Venezuela',
    'México',
    'Otro'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre_completo || !formData.documento_identidad || 
        !formData.telefono || !formData.email || !formData.razon_social) {
      toast.error('Por favor completá los campos obligatorios (*)');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Por favor ingresá un email válido');
      return;
    }

    setLoading(true);
    try {
      await api.post('/solicitudes/taller-itinerante', formData);
      toast.success('¡Solicitud enviada correctamente! Nos pondremos en contacto pronto.');
      setFormData({
        nombre_completo: '',
        documento_identidad: '',
        telefono: '',
        pais: '',
        provincia: '',
        direccion: '',
        email: '',
        razon_social: '',
        numero_anac: '',
        pagina_web: '',
        aerodromo: '',
        contacto_email: true,
        contacto_telefono: false,
        comentarios: ''
      });
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error(formatApiError(error, 'Error al enviar la solicitud'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-teal-700">
            <Plane className="h-6 w-6" />
            Solicitud de Suscripción Personalizada
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-2">
            Para talleres aeronáuticos itinerantes o sin certificación de capacidad operativa 
            específica emitida por la autoridad aeronáutica nacional.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Datos Personales */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
              <User className="h-4 w-4 text-teal-600" />
              Datos Personales
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                <Input
                  id="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={(e) => handleChange('nombre_completo', e.target.value)}
                  placeholder="Juan Pérez"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="documento_identidad">Documento de Identidad *</Label>
                <Input
                  id="documento_identidad"
                  value={formData.documento_identidad}
                  onChange={(e) => handleChange('documento_identidad', e.target.value)}
                  placeholder="DNI / CI / Pasaporte"
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefono" className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> Teléfono *
                </Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  placeholder="+598 99 123 456"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Correo Electrónico *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@ejemplo.com"
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pais" className="flex items-center gap-1">
                  <Globe className="h-3 w-3" /> País
                </Label>
                <Select value={formData.pais} onValueChange={(v) => handleChange('pais', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {paises.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="provincia">Provincia / Departamento</Label>
                <Input
                  id="provincia"
                  value={formData.provincia}
                  onChange={(e) => handleChange('provincia', e.target.value)}
                  placeholder="Montevideo"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="direccion" className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Dirección
                </Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => handleChange('direccion', e.target.value)}
                  placeholder="Av. Principal 123"
                />
              </div>
            </div>
          </div>

          {/* Datos del Taller */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
              <Building2 className="h-4 w-4 text-teal-600" />
              Datos del Taller
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="razon_social">Razón Social / Nombre de Fantasía *</Label>
                <Input
                  id="razon_social"
                  value={formData.razon_social}
                  onChange={(e) => handleChange('razon_social', e.target.value)}
                  placeholder="Taller Aeronáutico XYZ"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="numero_anac">Nº de Certificación (si aplica)</Label>
                <Input
                  id="numero_anac"
                  value={formData.numero_anac}
                  onChange={(e) => handleChange('numero_anac', e.target.value)}
                  placeholder="Opcional"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pagina_web">Página Web</Label>
                <Input
                  id="pagina_web"
                  value={formData.pagina_web}
                  onChange={(e) => handleChange('pagina_web', e.target.value)}
                  placeholder="www.ejemplo.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="aerodromo" className="flex items-center gap-1">
                  <Plane className="h-3 w-3" /> Aeródromo
                </Label>
                <Input
                  id="aerodromo"
                  value={formData.aerodromo}
                  onChange={(e) => handleChange('aerodromo', e.target.value)}
                  placeholder="Aeropuerto / Base de operaciones"
                />
              </div>
            </div>
          </div>

          {/* Preferencia de Contacto */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2">
              ¿Cómo preferís ser contactado?
            </h3>
            
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="contacto_email"
                  checked={formData.contacto_email}
                  onCheckedChange={(checked) => handleChange('contacto_email', checked)}
                />
                <Label htmlFor="contacto_email" className="cursor-pointer">
                  Email
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="contacto_telefono"
                  checked={formData.contacto_telefono}
                  onCheckedChange={(checked) => handleChange('contacto_telefono', checked)}
                />
                <Label htmlFor="contacto_telefono" className="cursor-pointer">
                  Llamada telefónica
                </Label>
              </div>
            </div>
          </div>

          {/* Comentarios */}
          <div className="space-y-2">
            <Label htmlFor="comentarios">Comentarios o consultas adicionales</Label>
            <Textarea
              id="comentarios"
              value={formData.comentarios}
              onChange={(e) => handleChange('comentarios', e.target.value)}
              placeholder="Contanos sobre tu taller, qué tipo de aeronaves trabajás, qué documentación necesitás..."
              rows={3}
            />
          </div>

          {/* Nota informativa */}
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-sky-800">
            <p className="font-medium mb-1">📋 ¿Qué sucede después?</p>
            <p>
              Una vez recibida tu solicitud, nuestro equipo se pondrá en contacto 
              para diseñar un plan personalizado según tus necesidades específicas.
            </p>
          </div>

          {/* Botón Submit */}
          <Button 
            type="submit" 
            className="w-full bg-teal-600 hover:bg-teal-700"
            disabled={loading}
          >
            {loading ? (
              <>Enviando...</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar Solicitud
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FormularioTallerItinerante;
